import axios from 'axios';

import loginService from '../components/Login/loginService';
import { STORAGE_KEYS } from '../constants/storageKeys';
import tokenClass from '../constants/tokenClass';

import logger from './logger';

// Request queue for handling concurrent 401 responses
// Maximum number of requests that can be queued during token refresh
const MAX_QUEUE_SIZE = 50;
let isRefreshing = false;
let failedQueue = [];

/**
 * Helper function to check if redirect to login is needed
 * @returns {boolean} True if user should be redirected to login page
 */
const shouldRedirectToLogin = () => {
  return (
    window.location.pathname !== '/' &&
    !window.location.pathname.includes('/#/')
  );
};

/**
 * Processes all queued requests after token refresh
 * @param {Error|null} error - Error to reject all queued requests with, or null if successful
 * @param {string|null} token - New access token to use for queued requests, or null if refresh failed
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      if (config) {
        // Ensure headers object exists
        if (!config.headers) {
          config.headers = {};
        }
        config.headers.Authorization = `Bearer ${token}`;
        resolve(axiosInstance(config));
      } else {
        // Reject if config is missing - this shouldn't happen in normal flow
        reject(
          new Error(
            'Invalid request configuration in queue. Please retry your request.',
          ),
        );
      }
    }
  });
  failedQueue = [];
};

const axiosInstance = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = tokenClass.getTokenFromSession();

    // Don't inject token for login or refresh token endpoints
    if (
      token &&
      !config.url?.includes('/v1/user/token') &&
      !config.url?.includes('/v1/user/refresh')
    ) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (!config.headers) {
      config.headers = {};
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    config.headers = {
      ...config.headers,
    };
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const { response } = error;

    if (response) {
      switch (response.status) {
        case 401: {
          const refreshToken = sessionStorage.getItem(
            STORAGE_KEYS.REFRESH_TOKEN,
          );
          const originalRequest = error.config;

          // Don't attempt refresh if the request itself is the refresh endpoint
          // This prevents infinite loops if refresh token is invalid
          if (
            originalRequest.url?.includes('/v1/user/refresh') ||
            originalRequest.url?.includes('/v1/user/token')
          ) {
            sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
            sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

            if (shouldRedirectToLogin()) {
              window.location.href = '/';
            }

            return Promise.reject({
              ...error,
              message: 'Authentication failed. Please login again.',
              isUnauthorized: true,
            });
          }

          if (refreshToken && !originalRequest._retry) {
            originalRequest._retry = true;

            // If already refreshing, queue this request
            if (isRefreshing) {
              return new Promise((resolve, reject) => {
                // Prevent memory leak by limiting queue size
                if (failedQueue.length >= MAX_QUEUE_SIZE) {
                  const oldest = failedQueue.shift();
                  const overflowError = new Error(
                    'Request queue overflow. Please try again.',
                  );
                  logger.warn(
                    '[Token Refresh] Queue overflow detected. Rejecting oldest request.',
                    { queueSize: failedQueue.length, maxSize: MAX_QUEUE_SIZE },
                  );
                  oldest.reject(overflowError);
                }
                failedQueue.push({ resolve, reject, config: originalRequest });
              });
            }

            // Start refresh process - set flag before async operation to prevent race conditions
            isRefreshing = true;

            return loginService
              .refreshToken(refreshToken)
              .then((data) => {
                const newToken = data.access_token;
                if (!newToken) {
                  throw new Error(
                    'No access token received from refresh endpoint',
                  );
                }

                sessionStorage.setItem(STORAGE_KEYS.TOKEN, newToken);
                const newRefreshToken = data.refresh_token;
                if (newRefreshToken) {
                  sessionStorage.setItem(
                    STORAGE_KEYS.REFRESH_TOKEN,
                    newRefreshToken,
                  );
                }

                // Ensure headers object exists before setting Authorization
                if (!originalRequest.headers) {
                  originalRequest.headers = {};
                }
                originalRequest.headers.Authorization = `Bearer ${newToken}`;

                // Process all queued requests with new token
                // Wrap in try-catch to ensure isRefreshing flag is always reset
                try {
                  processQueue(null, newToken);
                } catch (queueError) {
                  logger.error(
                    '[Token Refresh] Error processing request queue:',
                    queueError,
                  );
                  // Still continue with the original request even if queue processing fails
                }

                return axiosInstance(originalRequest);
              })
              .catch((err) => {
                // Reject all queued requests with the refresh error
                const refreshError = err.isRefreshError
                  ? err
                  : {
                    ...err,
                    message:
                        err.message ||
                        'Token refresh failed. Please login again.',
                    isRefreshError: true,
                  };

                // Wrap in try-catch to ensure isRefreshing flag is always reset
                try {
                  processQueue(refreshError, null);
                } catch (queueError) {
                  logger.error(
                    '[Token Refresh] Error processing request queue on refresh failure:',
                    queueError,
                  );
                }

                sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
                sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

                if (shouldRedirectToLogin()) {
                  window.location.href = '/';
                }

                return Promise.reject({
                  ...error,
                  message:
                    refreshError.message ||
                    'Your session has expired. Please login again.',
                  isUnauthorized: true,
                });
              })
              .finally(() => {
                isRefreshing = false;
              });
          }

          // No refresh token or retry already attempted
          sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
          sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

          if (shouldRedirectToLogin()) {
            window.location.href = '/';
          }

          return Promise.reject({
            ...error,
            message: 'Your session has expired. Please login again.',
            isUnauthorized: true,
          });
        }

        case 403:
          return Promise.reject({
            ...error,
            message: "You don't have permission to access this resource.",
            isForbidden: true,
          });

        case 404:
          return Promise.reject({
            ...error,
            message: 'The requested resource was not found.',
            isNotFound: true,
          });

        case 500:
          return Promise.reject({
            ...error,
            message: 'Server error. Please try again later.',
            isServerError: true,
          });

        default:
          return Promise.reject({
            ...error,
            message:
              error.response?.data?.detail ||
              error.message ||
              'An error occurred',
          });
      }
    } else if (error.request) {
      return Promise.reject({
        ...error,
        message: 'Network error. Please check your internet connection.',
        isNetworkError: true,
      });
    } else {
      return Promise.reject({
        ...error,
        message:
          error.message || 'An error occurred while setting up the request.',
      });
    }
  },
);

export default axiosInstance;
